import {
  Box,
  Grid,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  keyframes,
  visuallyHiddenStyle,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { SectionHead } from "@/components";
import { KNIT_BASICS, KNIT_BASICS_HEAD } from "@/content";
import { useNearOrIdle } from "@/hooks";
import { breakpoints } from "@/theme/foundations/breakpoints";

const { views } = KNIT_BASICS;

const fadeOut = keyframes({ from: { opacity: 1 }, to: { opacity: 0 } });

const closeUpStyle = { width: "100%", height: "auto", aspectRatio: "1 / 1", objectFit: "cover" } as const;

// Below xl the list and the close-up stack: each view's close-up opens under it instead, and the
// figure stays for screen readers only, as the tabs' panels.
const STACKED = `@media not all and (min-width: ${breakpoints.xl})`;

export const KnittingBasics = () => {
  const [index, setIndex] = useState(0);
  // The view that was showing, fading out over the one that replaces it.
  const [leaving, setLeaving] = useState<{ readonly index: number; readonly key: number } | null>(null);
  const changesRef = useRef(0);
  const sectionRef = useRef<HTMLElement>(null);
  // The close-ups load once the section is near or the page is idle, not with the page's own files.
  const ready = useNearOrIdle(sectionRef);

  const select = (next: number) => {
    if (next === index) return;
    changesRef.current += 1;
    setLeaving({ index, key: changesRef.current });
    setIndex(next);
  };

  // Fetch the other close-ups then too, so switching views never waits on one.
  useEffect(() => {
    if (!ready) return;
    views.forEach((view) => {
      const image = new Image();
      image.src = view.image.src;
    });
  }, [ready]);

  return (
    <Box ref={sectionRef} as="section" id="basics" maxW="maxw" mx="auto" px="pad" pb="sec">
      <SectionHead head={KNIT_BASICS_HEAD} />

      <Tabs variant="steps" orientation="vertical" index={index} onChange={select}>
        <Grid
          templateColumns={{ base: "minmax(0, 1fr)", xl: "minmax(0, 1fr) minmax(0, 1fr)" }}
          gap="clamp(32px, 4vw, 64px)"
          alignItems="start"
        >
          <TabList aria-label={KNIT_BASICS.label}>
            {views.map((view, viewIndex) => {
              const isActive = viewIndex === index;

              return (
                <Tab key={view.id} alignItems={{ base: "baseline", xl: "center" }}>
                  <Box
                    as="span"
                    fontSize="clamp(1rem, 1.4vw, 1.375rem)"
                    color={isActive ? "brand.600" : "warm.600"}
                    transitionProperty="color"
                    transitionDuration="0.3s"
                    transitionTimingFunction="brand"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {String(viewIndex + 1).padStart(2, "0")}
                  </Box>

                  <Box as="span" display="block" minW={0}>
                    <Heading
                      as="span"
                      variant="step"
                      display="block"
                      color={isActive ? "ink.DEFAULT" : "warm.600"}
                      transitionProperty="color"
                      transitionDuration="0.3s"
                      transitionTimingFunction="brand"
                    >
                      {view.title}
                    </Heading>
                    <Box
                      className="print-expand"
                      display="grid"
                      gridTemplateRows={isActive ? "1fr" : "0fr"}
                      mt={isActive ? "12px" : 0}
                      opacity={isActive ? 1 : 0}
                      transitionProperty="grid-template-rows, opacity, margin-top"
                      transitionDuration="0.4s"
                      transitionTimingFunction="brand"
                    >
                      <Box as="span" display="block" overflow="hidden" minH={0}>
                        <Box
                          as="span"
                          display="block"
                          fontSize="body"
                          lineHeight={1.35}
                          color="warm.600"
                          maxW="46ch"
                          textTransform="none"
                          letterSpacing="normal"
                          sx={{ textWrap: "pretty" }}
                        >
                          {view.body}
                        </Box>
                        <Box
                          as="span"
                          aria-hidden="true"
                          display={{ base: "block", xl: "none" }}
                          mt="18px"
                          borderRadius="lg"
                          overflow="hidden"
                          bg="surface.white"
                          border="1px solid"
                          borderColor="line.hairline"
                        >
                          <img
                            src={ready ? view.image.src : undefined}
                            width={view.image.width}
                            height={view.image.height}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            style={{ ...closeUpStyle, display: "block" }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Tab>
              );
            })}
          </TabList>

          <Box
            as="figure"
            className="print-avoid-break"
            position={{ xl: "sticky" }}
            top={{ xl: "104px" }}
            borderRadius="lg"
            overflow="hidden"
            bg="surface.white"
            border="1px solid"
            borderColor="line.hairline"
            sx={{ [STACKED]: visuallyHiddenStyle }}
          >
            <Box position="relative">
              <TabPanels>
                {views.map((view) => (
                  <TabPanel key={view.id} p={0}>
                    <img
                      src={ready ? view.image.src : undefined}
                      width={view.image.width}
                      height={view.image.height}
                      alt={view.image.alt}
                      loading="lazy"
                      decoding="async"
                      style={closeUpStyle}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
              {leaving && (
                <Box
                  key={leaving.key}
                  aria-hidden="true"
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  animation={`${fadeOut} 0.5s ease-in-out forwards`}
                  onAnimationEnd={() => setLeaving(null)}
                  sx={{ "@media (prefers-reduced-motion: reduce)": { display: "none" } }}
                >
                  <img
                    src={views[leaving.index].image.src}
                    width={views[leaving.index].image.width}
                    height={views[leaving.index].image.height}
                    alt=""
                    style={closeUpStyle}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Tabs>
    </Box>
  );
};
